import { Injectable, PLATFORM_ID, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import * as amplitude from '@amplitude/analytics-browser';
import { environment } from '../../../environments/environment';
import { PQRS_MTS_PEI_APP_NAME, PQRS_MTS_PEI_ID } from '../../analytics/pqrs-events';
import { User } from '../../shared/types/user.types';

@Injectable({
  providedIn: 'root'
})
export class AmplitudeService {
  private isBrowser: boolean;
  private platformId = inject(PLATFORM_ID);
  private lastSyncedUserId: string | null = null;
  
  // Estado del usuario actual usando signal para sincronización reactiva
  private currentUser = signal<User | null>(null);

  constructor() {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  updateCurrentUser(user: User | null): void {
    this.currentUser.set(user);
    this.syncUserIdentity();
  }

  init(initialUser?: User | null): void {
    if (!this.isBrowser || !environment.amplitude?.apiKey) {
      return;
    }

    try {
      // Actualizar el usuario actual si se proporciona
      if (initialUser !== undefined) {
        this.currentUser.set(initialUser);
      }
      
      const initialUserId = this.resolveUserId(this.currentUser());
      amplitude.init(environment.amplitude.apiKey, initialUserId, {
        autocapture: false // Desactivar autocapture que captura clicks y otros eventos automáticamente
      });
      this.syncUserIdentity();
    } catch (error) {
      console.error('Error inicializando Amplitude:', error);
    }
  }
   
  logEvent(eventName: string, eventProperties?: any): void {
    if (!this.isBrowser) return;
    try {
      // Sincronizar identidad del usuario antes de enviar el evento
      this.syncUserIdentity();
      amplitude.logEvent(eventName, eventProperties);
    } catch (error) {
      console.error(`Amplitude Service: Error al registrar evento ${eventName}.`, error);
    }
  }

  logEventWithUserId(eventName: string, eventProperties: any, userId: string): void {
    if (!this.isBrowser) return;
    try {
      // Guardar el userId actual antes de cambiarlo temporalmente
      const previousUserId = this.lastSyncedUserId;
      
      // Establecer el userId proporcionado temporalmente
      this.lastSyncedUserId = userId;
      amplitude.setUserId(userId);
      
      // Enviar el evento con el userId especificado
      amplitude.logEvent(eventName, eventProperties);
      
      // Restaurar el userId anterior si existía, o sincronizar con el usuario actual
      if (previousUserId !== null) {
        this.lastSyncedUserId = previousUserId;
        amplitude.setUserId(previousUserId);
      } else {
        // Si no había userId anterior, sincronizar con el estado actual
        this.syncUserIdentity();
      }
    } catch (error) {
      console.error(`Amplitude Service: Error al registrar evento ${eventName} con userId ${userId}.`, error);
    }
  }

  setUserId(userId: string | undefined): void {
    if (!this.isBrowser) return;
    amplitude.setUserId(userId);
  }

  setUserProperties(userProperties: amplitude.Types.IIdentify): void {
    if (!this.isBrowser) return;
    amplitude.identify(userProperties);
  }

  private syncUserIdentity(): void {
    if (!this.isBrowser) return;

    // Leer el valor actual del signal para obtener siempre el estado más reciente
    const user = this.currentUser();
    const targetUserId = this.resolveUserId(user);

    // Solo sincronizar si cambió el userId
    if (this.lastSyncedUserId === targetUserId) {
      return;
    }

    this.lastSyncedUserId = targetUserId;
    amplitude.setUserId(targetUserId);

    // Configurar propiedades del usuario
    const identify = new amplitude.Identify();
    identify.set('app', PQRS_MTS_PEI_APP_NAME);
    identify.set('is_authenticated', !!user);

    if (user) {
      if (user.name) identify.set('name', user.name);
      if (user.email) identify.set('email', user.email);
      if (user.buildingId) identify.set('building_id', user.buildingId);
      if (user.unitId) identify.set('unit_id', user.unitId);
    } else {
      identify.set('user_type', 'guest');
    }

    amplitude.identify(identify);
  }

  reset(): void {
    if (!this.isBrowser) return;
    amplitude.reset();
    this.lastSyncedUserId = null;
    this.currentUser.set(null);
  }

  private resolveUserId(user?: User | null): string {
    return user?.id || PQRS_MTS_PEI_ID;
  }
}
